import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { DateTime } from 'luxon';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
  getWhatsappTemplate,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Setting, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { sendWhatsapp } from './_helper.js';

// Mail template for Veranstaltungseintragung durch Admin

defineMailTemplate(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Veranstaltungseintragung durch den Admin',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>gute Neuigkeiten, soeben ist ein Platz in unserer Veranstaltung frei geworden. Da Sie auf dem n&auml;chsten Platz der Warteliste standen, haben wir Sie verbindlich in die Teilnehmer*innenliste eingetragen. Nachstehend f&uuml;hren wir nochmals die wesentlichen Informationen der Veranstaltung auf:</p>
<p>{{changed-registrations}}</p>
<p>Eine Auflistung all Ihrer Workshops und die M&ouml;glichkeit, sich wieder abmelden zu k&ouml;nnen, finden Sie in Ihrer Anmeldeverwaltung: {{login-link}}</p>
<p><strong>Sie k&ouml;nnen nicht teilnehmen?&nbsp;</strong></p>
<p>Falls die den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung. Eine Abmeldung ist nur bis sp&auml;testens 6 Tage vor Workshop-Beginn m&ouml;glich. Nach Ablauf dieser Frist ist eine Abmeldung nur <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a> mit Angabe eines triftigen Grundes m&ouml;glich. Bei Nichterscheinen ohne vorherige Absage behalten wir uns vor, Sie von weiteren Workshops auszuschlie&szlig;en bzw. Ihnen entstehende Kosten in Rechnung zu stellen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Good news, a place has just become available in our event. As you were on the next place on the waiting list, we have bindingly entered you in the list of participants.</p>
<p>Below we list again the essential information of the event:</p>
<p>{{changed-registrations}}</p>
<p>You will find a list of all your workshops and the possibility to cancel your registration in your registration administration: {{login-link}}</p>
<p><strong>You are unable to attend? Please deregister and thereby enable other students to participate.</strong> If you are unable to attend the event, please deregister promptly in your registration administration. Students on the waiting list will then benefit from your deregistration. Deregistration is only possible up to 6 days before the start of the workshop. After this deadline, cancellation is only possible by e-mail with a valid reason. In the case of non-attendance without prior cancellation, we reserve the right to exclude you from further workshops or to charge you for any costs incurred.</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN),
  attachments: [],
  displayName: 'Veranstaltungseintragung durch Admin',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN),
});

export async function notifyVeranstaltungseintragungDurchAdmin(participationID: string) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    state: 1,
    institutionID: 1,
    createdAt: 1,
    updatedAt: 1,
    participated: 1,
    productID: 1,
    user: {
      _id: 1,
      email: 1,
      firstName: 1,
      lastName: 1,
      title: 1,
    },
    waitingListPlace: 1,
    product: {
      _class: 1,
      _id: 1,
      name: 1,
      description: 1,
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      institutionID: 1,
      timeframeID: 1,
      lastReminderMailSent: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      capacity: 1,
      hideCapacity: 1,
      appointments: { start: 1, end: 1, location: 1, name: 1 },
      slots: { _id: 1, start: 1, end: 1 },
      settings: {
        ...Setting.allFields,
        blockAutomaticMoveUpDaysInAdvance: 1,
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
        blockSignOffDaysInAdvance: 1,
        inheritedBlockSignOffDaysInAdvance: 1,
      },
    },
  });
  if (!participation) throw new Error('no participation found');
  const user = await getByID(
    User,
    participation.userID as string,
  )({
    _id: 1,
    email: 1,
    firstName: 1,
    lastName: 1,
    title: 1,
    username: 1,
    isLdapAccount: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    suspentions: { _id: 1, institutionID: 1 },
    receivesNewsletter: 1,
    roles: { _id: 1, name: 1, contexts: 1, permissions: 1 },
  });
  if (!user) throw new Error('no user found');

  // Get institution with color definitions
  const institution = await getByID(
    Institution,
    participation.product?.institutionID as string,
  )({
    ...Institution.allFields,
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('no institution found');

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    participation.product?.institutionID,
    MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN,
  )(InstitutionMailTemplate.allFields);

  if (!mailTemplate) throw new Error('no mail template found');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = participation.product?.appointments?.[0]?.start;
  const daysTillEvent = start?.diff(DateTime.now()).as('days').toFixed(0);
  let dates = participation.product?.appointments?.map(a => a.start.toFormat('dd.LL.yyyy')).join(', ');
  if (!dates) dates = start.toFormat('dd.LL.yyyy');

  if (user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification) {
    const whatsapp_template = getWhatsappTemplate(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN);
    await sendWhatsapp({
      to: user.whatsappPhone,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: whatsapp_template.name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: participation.product?.name || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text:
                    participation.product?._class === Workshop._class
                      ? InstitutionMailTemplate.workshopTerminString(participation.product)
                      : InstitutionMailTemplate.einzelberatungTerminString(participation.product, participation),
                },
              ],
            },
          ],
        },
      },
    });
  } else if (mailTemplate.emailNotification) {
    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name ?? '',
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state,
          participation: participation as Participation | undefined,
          institution,
          englishVersion: false,
          newWaitingListPlace: participation.waitingListPlace,
        }),
      ],
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state as ParticipationState,
          newWaitingListPlace: participation.waitingListPlace,
          participation: participation as Participation | undefined,
          institution,
          englishVersion: true,
        }),
      ],
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    return await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID: participation.product?.institutionID,
    });
  }
}
